import { useFpsStore } from './fpsGuard';

/**
 * Plan 07-05 — DOM toast paired with FpsGuardProbe (fpsGuard.ts).
 *
 * Renders a period-chrome notice with a "Switch to Read the Tour" escape
 * hatch when sustained FPS drops below 40 for 3+ seconds. User can dismiss;
 * store logic clears the dismiss flag when FPS recovers so a fresh episode
 * can re-surface the toast without nagging inside a single degraded window.
 *
 * Chrome matches BoardingOverlay + SourcesPanel + EraToggle — cream FDF6EE /
 * espresso 3B2F2F / warm-ochre D4A87E border, Goudy Old Style serif stack.
 * role="status" + aria-live="polite" so screen readers get the notice without
 * interrupting the current utterance.
 *
 * Mount in UIShell (DOM-side). Gated entirely by store state — zero render
 * cost when degraded === false.
 */
export function FpsGuardToast() {
  const degraded = useFpsStore((s) => s.degraded);
  const dismissed = useFpsStore((s) => s.dismissed);
  const dismiss = useFpsStore((s) => s.dismiss);

  if (!degraded || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="fps-guard-toast"
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 180,
        background: '#FDF6EE',
        color: '#3B2F2F',
        border: '1px solid #D4A87E',
        fontFamily: '"Goudy Old Style", "Cormorant Garamond", Georgia, serif',
        fontSize: '0.95rem',
        lineHeight: 1.5,
        padding: '0.75rem 1.25rem',
        maxWidth: 460,
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      }}
    >
      <span style={{ flex: 1 }}>
        This browser may be straining to render the 3D scene smoothly. You can{' '}
        <a
          href="/read"
          style={{ color: '#3B2F2F', textDecoration: 'underline' }}
        >
          switch to Read the Tour
        </a>{' '}
        for a faster text + photograph version.
      </span>
      <button
        type="button"
        aria-label="Dismiss performance notice"
        onClick={() => dismiss()}
        style={{
          background: 'transparent',
          border: 'none',
          fontFamily: 'inherit',
          fontSize: '1.1rem',
          color: '#3B2F2F',
          cursor: 'pointer',
          padding: '0 0.25rem',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
